"use client"
import { imageUrl } from "@/util/api/baseUrl";
import { ConvertLink } from "@/util/tools";
import { PersianNum } from "@/util/tools/PersianNum";
import ExpandText from "@/util/view/ExpandText";
import { Star } from "lucide-react";
import dynamic from "next/dynamic";
import Link from "next/link";
import MainAttribute from "./components/attribute/MainAttribute";
import BreadcrumbProducts from "./components/breadcrumb/BreadcrumbProducts";
import CommentList from "./components/comments/CommentList";
import DynamicImage from "./components/DynamicImage";
import EssenceList from "./components/essence/EssenceList";
import GalleryImages from "./components/gallery/GalleryImages";
import ProductPrice from "./components/price/ProductPrice";
import ProductAudio from "./components/ProductAudio";
import ProductVideo from "./components/ProductVideo";
import ProductsByBrand from "./components/simlar/ProductsByBrand";
import ProductsByListIds from "./components/simlar/ProductsByListIds";
import { CommentsType, ParseUrl, ProductURLTypeData } from "./product";

const ShareLink = dynamic(() => import("./components/share/ShareLink"), {
    ssr: false
})
const FavoriteButton = dynamic(() => import("./components/favorite/FavoriteButton"), {
    ssr: false
})
const SellButton = dynamic(() => import("./components/sellButton/SellButton"), {
    ssr: false
})
const StickySellButton = dynamic(() => import("./components/sellButton/StickySellButton"), {
    ssr: false
})


interface Props {
    product: ProductURLTypeData;
    comments: CommentsType;
    commentOpen: boolean;
    averageRates: string;
    attr: ParseUrl
}

const ProductsView = ({
    comments,
    product,
    averageRates,
    attr
}: Props) => {
    const productNames = () => {
        const x = product?.product_title;
        const attri = product?.ProductIdentities.filter(
            (i) => i.id === attr.pi
        )[0]?.ProdProperties[0]?.Attribute.attribute_name;
        return x + " " + attri;
    };

    const prodName = productNames();

    const brand = product?.ProductIdentities.filter(
        (i) => i.id === attr.pi
    )[0]?.Brand;

    const sex = product?.ProdProperties.filter(
        (i) => i.Property.property_name === "جنسیت"
    );
    const man = sex?.find((i) => i.Attribute.attribute_name === "مردانه");
    const woman = sex?.find((i) => i.Attribute.attribute_name === "زنانه");


    return (
        <div>
            <div className="pt-5">
                <BreadcrumbProducts linkCategory={man ? "/categories/mens-perfume" : "/categories/womens-perfume"} nameProduct={prodName} nameCategory={man ? "مردانه" : "زنانه"} />
                <hr className="mb-3 mt-1 container" />
                <div className="flex flex-wrap sm:container ">
                    <div className="md:w-1/2 w-full">
                        <GalleryImages data={product} prodIID={attr.pi} />
                    </div>
                    <div className="md:w-1/2 w-full sm:m-0 mt-2">
                        <div className="flex justify-between px-2">
                            <div>
                                <h1 className="font-semibold text-text sm:text-2xl">{product?.product_title}</h1>
                                <p className="sm:font-semibold text-slate-600 text-sm">{product?.product_title_second}</p>
                            </div>
                            <div className="flex">
                                <FavoriteButton prodId={product?.id} />
                                <ShareLink />
                            </div>
                        </div>
                        <hr />
                        <div className="flex flex-wrap">
                            <div className="sm:w-1/2 w-full">
                                <MainAttribute
                                    woman={!!woman?.id}
                                    man={!!man?.id}
                                    data={product?.ProdProperties} />
                            </div>
                            <div className="sm:w-1/2 w-full">
                                <div className={`flex flex-wrap items-center justify-between rounded-lg py-1 px-3 m-1 text-right`}>
                                    <p className="font-bold text-text text-sm text-nowrap">
                                        برند:
                                    </p>
                                    <Link href={`/brand/${ConvertLink(brand?.brand_url)}`} className="text-blue-800 text-sm flex items-center">
                                        <span>{brand?.brand_title} - {brand?.brand_title_second}</span>
                                    </Link>
                                </div>
                                <div className={`flex flex-wrap items-center justify-between rounded-lg py-1 px-3 m-1 text-right`}>
                                    <p className="font-bold text-text text-sm text-nowrap">
                                        دسته بندی:
                                    </p>
                                    <Link href={man ? "/categories/mens-perfume" : "/categories/womens-perfume"} className="text-blue-800 text-sm flex items-center">
                                        <span>{man ? "مردانه" : "زنانه"}</span>
                                    </Link>
                                </div>
                                <div className={`flex flex-wrap items-center justify-between rounded-lg py-1 px-3 m-1 text-right`}>
                                    <p className="font-bold text-text text-sm text-nowrap">
                                        غلظت:
                                    </p>
                                    <p className="text-text1 text-sm flex items-center">
                                        {
                                            product?.ProductIdentities.filter(
                                                (i) => i.id === attr.pi
                                            )[0]?.product_perfume
                                        }
                                    </p>
                                </div>
                                <div className={`flex flex-wrap items-center justify-between rounded-lg py-1 px-3 m-1 text-right`}>
                                    <p className="font-bold text-text text-sm text-nowrap">
                                        تعداد دیدگاه:
                                    </p>
                                    <p className="text-text1 text-sm flex items-center">
                                        {
                                            PersianNum(comments?.data?.total)}
                                    </p>
                                </div>
                                <div className={`flex flex-wrap items-center justify-between rounded-lg py-1 px-3 m-1 text-right`}>
                                    <p className="font-bold text-text text-sm text-nowrap">
                                        امتیاز:
                                    </p>
                                    <p className="text-text1 text-sm flex items-center">
                                        <Star size={14} className="mx-1" />
                                        {PersianNum(Number(averageRates) || 0)}
                                    </p>
                                </div>
                            </div>
                        </div>
                        <hr />
                        {product?.product_type !== "simple" && (
                            <ProductPrice
                                data={product?.ProductIdentities}
                                albums={product?.Albums}
                                attr={attr}
                            />
                        )}
                        <SellButton ProdIId={attr.pi} productId={product?.id} prodName={prodName} similar={product?.product_similars} brandId={brand?.id} />
                        <hr />
                        <ProductAudio product_audio={imageUrl + product?.product_audio} product_audio_alt={product?.product_audio_alt} product_audio_title={product?.product_audio_title} />
                    </div>
                </div>
            </div>
            <hr className="mb-3 mt-1 container" />
            <div className="sm:container flex flex-wrap items-start">
                <div className="w-full p-2">
                    <div className="text-justify" dangerouslySetInnerHTML={{ __html: product?.product_detail }} />
                </div>
            </div>
            <div>
                <ProductVideo
                    product_video={product?.product_video ? imageUrl + product?.product_video : "/soon.mp4"}
                    product_video_alt={product?.product_video_alt}
                    product_video_title={product?.product_video_title}
                    product_video_detail={product?.product_video_detail}
                />
            </div>

            <div className="sm:container flex flex-wrap my-4">
                <div className="sm:w-1/2 w-full">
                    <div className="border rounded-md p-2 m-2 bg-main">
                        <p className="font-bold text-xl my-1 text-center text-text">اسانس ها</p>
                        <EssenceList style product={product} />
                    </div>
                </div>
                <div className="sm:w-1/2 w-full">
                    <div className="border rounded-md p-2 m-2">
                        <h4 className="font-bold text-xl my-5 text-text text-center">سوالات متداول</h4>
                        {
                            product?.ProductQAs.map((q) =>
                                <div key={q.id} className="bg-main my-1 shadow rounded-lg p-3 ">
                                    <p className="font-bold text-text">{q.product_question}</p>
                                    <p className="text-text1">{q.product_answer}</p>
                                </div>
                            )
                        }
                    </div>
                </div>
            </div>
            <div>
                <DynamicImage title={product?.product_banner_title} alt={product?.product_banner_alt ? product?.product_banner_alt : product?.product_image_second} src={product?.product_banner ? imageUrl + product?.product_banner : imageUrl + product?.product_image_second} text={product?.product_banner_detail} />
            </div>
            <ExpandText>
                <div className="sm:container product-content" dangerouslySetInnerHTML={{ __html: product?.product_description }} />
            </ExpandText>
            <CommentList data={comments} productIId={attr.pi} productId={product?.id} />
            {
                product?.product_similars ?
                    <div className="sm:container">
                        <ProductsByListIds ids={product?.product_similars} title={`عطر های مشابه ${product?.product_title}`} />
                    </div>
                    :
                    <div className="sm:container">
                        <ProductsByBrand idBrand={brand?.id} url={brand?.brand_url} />
                    </div>
            }
            <StickySellButton ProdIId={attr.pi} productId={product?.id} prodName={prodName} similar={product?.product_similars} brandId={brand?.id} />
        </div>
    )
}

export default ProductsView